import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemBreakout from "Reporting/Components/ReportItemBreakout";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";

/*
 * Table displaying different items in a report with the functionality to sort and filter
 */
export default function ReportTableBreakout({
  reports,
  title,
  onSortedReportsChange,
}) {
  const PAGE_HEIGHT_PX = 425;
  const tableRef = useRef(null);
  const GROUPED_TOTAL_PERCENT = 30;
  const PIVOT_TOTAL_PERCENT = 100 - GROUPED_TOTAL_PERCENT; // 70

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([{ field: "Last Name", direction: "asc" }]);

  const [sortedReports, setSortedReports] = useState(reports || []);
  const [totalItem, setTotalItem] = useState({
    IBO: "Total",
    lastName: "",
    displayName: "",
    pin: "",
    email: "",
    topicsId: "",
    diamond: "",
    attending: "",
    qGroups: [],
    columns: [],
  });

  const groupedHeaders = useMemo(() => {
    // Flatten all columns across reports
    const allCols = reports.flatMap((r) =>
      (r.columns || []).map((c) => ({ ...c }))
    );

    // Deduplicate headers by headerName+headerSort
    const uniqueHeaders = Array.from(
      new Map(allCols.map((col) => [col.headerName, col])).values()
    ).sort((a, b) => Number(a.headerSort) - Number(b.headerSort));

    return uniqueHeaders.map((header) => {
      const matchingColumns = allCols.filter(
        (col) => col.headerName === header.headerName
      );

      // Deduplicate columns by name
      const uniqueColumns = Array.from(
        new Map(matchingColumns.map((col) => [col.name, col])).values()
      );

      uniqueColumns.sort((a, b) => Number(a.sort) - Number(b.sort));

      return {
        headerName: header.headerName,
        headerSort: header.headerSort,
        columns: uniqueColumns,
        colSpan: uniqueColumns.length,
      };
    });
  }, [reports]);

  const allColumns = useMemo(() => {
    return groupedHeaders.flatMap((g) =>
      (g.columns || []).map((c) => ({ ...c, headerName: g.headerName }))
    );
  }, [groupedHeaders]);

  const totalGroupedColumns = groupedHeaders.reduce(
    (sum, h) => sum + (h.colSpan || h.columns.length || 0),
    0
  );
  const perColumnPercent =
    totalGroupedColumns > 0 ? GROUPED_TOTAL_PERCENT / totalGroupedColumns : 0;

  useEffect(() => {
    if (!sortedReports || sortedReports.length === 0) {
      setTotalItem(null);
      return;
    }

    const newTotal = {
      IBO: "Total",
      lastName: "",
      displayName: "",
      pin: "",
      email: "",
      topicsId: "",
      diamond: "",
      attending: "",
      qGroups: [],
      columns: allColumns.map((col) => {
        const total = sortedReports.reduce((sum, r) => {
          const colVal = r.columns.find(
            (c) => c.name === col.name && c.headerName === col.headerName
          )?.value;
          const numVal = Number(colVal);
          return sum + (isNaN(numVal) ? 0 : numVal);
        }, 0);

        return { ...col, value: total };
      }),
    };

    setTotalItem(newTotal);
  }, [sortedReports, allColumns]);

  useEffect(() => {
    let result = [...reports];

    // apply sorting (multi-sort)
    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const s of sort) {
          if (!s.field || s.direction === "none") continue;

          let aVal, bVal;
          switch (s.field) {
            case "IBO":
              aVal = a.IBO;
              bVal = b.IBO;
              break;
            case "Last Name":
              aVal = a.lastName;
              bVal = b.lastName;
              break;
            case "PIN":
              aVal = a.pin;
              bVal = b.pin;
              break;
            case "Diamond":
              aVal = a.diamond;
              bVal = b.diamond;
              break;
            default:
              aVal = a.columns.find((col) => col.name === s.field)?.value || 0;
              bVal = b.columns.find((col) => col.name === s.field)?.value || 0;
          }

          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if (aVal < bVal) return s.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return s.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedReports(result);
    if (onSortedReportsChange) {
      onSortedReportsChange(result);
    }
  }, [reports, sort, onSortedReportsChange]);

  const toggleSort = (field) => {
    setSort((prev) => {
      const existingIndex = prev.findIndex((s) => s.field === field);
      let newSort;

      if (existingIndex === -1) {
        newSort = [...prev, { field, direction: "asc" }];
      } else {
        const existing = prev[existingIndex];
        let nextDirection;
        if (existing.direction === "asc") nextDirection = "desc";
        else if (existing.direction === "desc") nextDirection = "none";
        else nextDirection = "asc";

        if (nextDirection === "none") {
          newSort = prev.filter((_, i) => i !== existingIndex);
        } else {
          newSort = [...prev];
          newSort[existingIndex] = { field, direction: nextDirection };
        }
      }

      return newSort;
    });
  };

  const renderSortIcon = (field) => {
    const index = sort.findIndex((s) => s.field === field);
    if (index === -1 || sort[index].direction === "none") {
      return <FontAwesomeIcon icon={faMinus} className={styles.menuChevron} />;
    }

    const icon =
      sort[index].direction === "asc"
        ? faChevronUp
        : sort[index].direction === "desc"
        ? faChevronDown
        : faMinus;

    return (
      <div className={styles.sortWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.menuChevron} />
        <span className={styles.sortPriority}>{index + 1}</span>
      </div>
    );
  };

  // Calculate page breaks for PDF/export
  const calculateBreaks = (ref, rowCount, setBreaks) => {
    if (!ref.current) {
      console.warn("Ref not available for break calculation");
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      // Get both the header and the title (headerInfo) heights
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      // headerInfo is outside the tableRef, so get it from the parent
      let headerInfoHeight = 0;
      const parent = ref.current.parentElement;
      if (parent) {
        const headerInfoEl = parent.querySelector(`.${styles.headerInfo}`);
        if (headerInfoEl)
          headerInfoHeight = headerInfoEl.getBoundingClientRect().height;
      }

      let staticHeight = headerInfoHeight + headerHeight;
      // If headerInfoHeight is 0, manually add 50px for the title/header
      if (headerInfoHeight === 0) {
        staticHeight += 50;
        // console.log(
        //   "[PageBreak] headerInfoHeight missing, adding 50px manually"
        // );
      }
      // console.log(
      //   "[PageBreak] headerHeight:",
      //   headerHeight,
      //   "headerInfoHeight:",
      //   headerInfoHeight,
      //   "staticHeight:",
      //   staticHeight
      // );

      let heightSoFar = 0;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        // console.log(`[PageBreak] Row ${i} height:`, rect.height);
        if (isFirstPage) {
          heightSoFar += staticHeight;
          isFirstPage = false;
        }
        // Check if adding this row would exceed the page height
        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          // console.log(
          //   `[PageBreak] --- PAGE BREAK at row ${currentRowIndex}, resetting heightSoFar to row height:`,
          //   rect.height
          // );
          heightSoFar = rect.height;
        } else {
          heightSoFar += rect.height;
        }
        // console.log(`[PageBreak] After row ${i}, heightSoFar:`, heightSoFar);
        currentRowIndex++;
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(
      tableRef,
      sortedReports.length + (totalItem ? 1 : 0),
      setPageBreakIndexes
    );
  }, [sortedReports, totalItem]);

  return (
    <div>
      {/* {index !== 0 && <div className={styles.pageBreak} />} */}
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>{title}</div>
        </div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.upperHeader}>
            <div
              className={styles.upperHeaderFiller}
              style={{
                flex: `0 0 ${PIVOT_TOTAL_PERCENT}%`,
                maxWidth: `${PIVOT_TOTAL_PERCENT}%`,
              }}
            />
            {groupedHeaders.map((header) => {
              const headerColCount =
                header.colSpan || header.columns.length || 0;
              const widthPercent = headerColCount * perColumnPercent;

              return (
                <div
                  key={header.headerName}
                  className={styles.headerGroup}
                  style={{
                    flex: `0 0 ${widthPercent}%`,
                    maxWidth: `${widthPercent}%`,
                  }}
                >
                  <span className={styles.headerLabel}>
                    {header.headerName}
                  </span>
                </div>
              );
            })}
          </div>

          <div className={styles.header}>
            <div className={styles.ibo} onClick={() => toggleSort("IBO")}>
              IBO{renderSortIcon("IBO")}
            </div>
            <div
              className={styles.lastName}
              onClick={() => toggleSort("Last Name")}
            >
              Last Name{renderSortIcon("Last Name")}
            </div>
            <div className={styles.displayName}>IBOs</div>
            <div className={styles.pin} onClick={() => toggleSort("PIN")}>
              PIN{renderSortIcon("PIN")}
            </div>
            <div className={styles.email}>Email</div>
            <div className={styles.topicsId}>Topic ID</div>
            <div
              className={styles.diamond}
              onClick={() => toggleSort("Diamond")}
            >
              Diamond{renderSortIcon("Diamond")}
            </div>
            <div className={styles.attending}>Attending Event</div>
            <div className={styles.qGroups}>QGroups</div>
            {allColumns.map((col, index) => (
              <div
                key={`${col.name}-${index}`}
                className={index % 2 !== 0 ? styles.col : styles.oddCol}
                style={{
                  flex: `1 1 0`,
                  minWidth: 0,
                  width: `${perColumnPercent}%`,
                }}
              >
                {col.name}
              </div>
            ))}
          </div>

          {totalItem && (
            <React.Fragment key="total">
              {pageBreakIndexes.includes(sortedReports.length) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemBreakout
                  item={totalItem}
                  isOdd={sortedReports.length % 2 !== 0}
                  type="total"
                  allColumns={allColumns}
                  groupedTotalPercent={GROUPED_TOTAL_PERCENT}
                  groupedColumnsCount={totalGroupedColumns}
                />
              </div>
            </React.Fragment>
          )}

          {sortedReports.map((row, i) => (
            <React.Fragment key={i}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemBreakout
                  item={row}
                  isOdd={i % 2 !== 0}
                  type="row"
                  allColumns={allColumns}
                  groupedTotalPercent={GROUPED_TOTAL_PERCENT}
                  groupedColumnsCount={totalGroupedColumns}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableBreakout.propTypes = {
  item: PropTypes.shape({
    eventOwner: PropTypes.string,
    eventType: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    events: PropTypes.arrayOf(PropTypes.object),
  }),
};
